import ManagerRepository from 'coreClasses/ManagerRepository';

import {
	Product as ProductType,
	RecommendationTypes,
	Recommendation as ProductRecommendation,
	ThreeDimensionalModelConfig,
	VariationGroup,
	Variant,
} from '../../store/data/products/types';
import VariantModel from './VariantModel';
import AbstractProductModel from './AbstractProductModel';
import ProductPriceModel from './Product/ProductPriceModel';
import ProductPromotionModel from './Product/ProductPromotionModel';

import {PRODUCT_DISTINCT_SIZES} from 'brandConstants';

/**
 * Represents ProductModel
 */
class ProductModel extends AbstractProductModel {
	type: ProductType;
	variants: VariationGroup[];
	slugUrl?: string;

	/**
	 * @constructs
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {ProductType} product - product
	 */
	constructor(managerRepository: ManagerRepository, product: ProductType) {
		super(managerRepository, product);
	}

	/**
	 * Check if all variants are out of stock.
	 * @returns {Boolean} True if all variants are out of stock
	 */
	areAllVariantsOutOfStock(): boolean {
		const allVariants = this.getVariants();
		const variantsLoaded = !!allVariants.length;

		if (!variantsLoaded) {
			return false;
		}

		return allVariants.every((variant: VariantModel) => variant.isReady() && !variant.isOrderable());
	}

	/**
	 * Returns variant object for the product.
	 * @param {String} variationID Specific variation ID
	 * @returns {VariantModel} Variant object
	 */
	getVariant(variationID: string): VariantModel {
		const variant = this.product?.fullVariants?.find((variant) => variant.id === variationID);

		return variant ? new VariantModel(this.managerRepository, variant) : null;
	}

	/**
	 * Returns the first variant object in stock
	 * @returns {VariantModel} Variant object
	 */
	getOrderableVariant(): VariantModel {
		const variants = this.getVariants();

		return variants?.length ? variants.find((variant) => !variant?.isOutOfStock()) : null;
	}

	/**
	 * Returns orderable status for the product.
	 * @returns {Boolean} True if the product is orderable
	 */
	isOrderable(): boolean {
		if (this.isBundleKitType()) {
			return this.product?.inventory?.orderable && this.product?.inventories?.[0]?.orderable;
		}

		if (this.isVariationGroup()) {
			return this.product?.variationGroups?.find((variationGroup) => variationGroup.productId === this.product?.id)
				?.orderable;
		}

		return this.product?.master?.orderable;
	}

	/**
	 * Returns preorderable status for the product.
	 * @returns {Boolean} True if the product is preorderable or at least one variant is preorderable
	 */
	hasPreorder(): boolean {
		return this.getVariants()?.some((variantModel) => variantModel.isPreorder());
	}

	/**
	 * Returns preorderable status for the product.
	 * @returns {Boolean} True if the product or all variants is preorderable
	 */
	isPreorder(): boolean {
		const variants = this.getVariants();

		// False if the product doesn't have variants
		if (!variants?.length) {
			return false;
		}

		return variants?.every((variantModel) => variantModel.isPreorder());
	}

	/**
	 * Checks if to show preorder for the product.
	 * @param {Boolean} allVariants true if all product variants need to be pre-orderable
	 * @returns {Boolean} True to show preorder for the product
	 */
	isPreOrderShown(allVariants: boolean = true): boolean {
		if (this.isBundleKitType()) {
			if (this.product?.inventory?.preorderable) {
				const inventory = this.product.inventory;

				return inventory.preorderable && inventory.ats !== 0 && inventory.stockLevel === 0;
			} else if (this.product?.inventories?.[0]?.preorderable) {
				const inventory = this.product.inventories[0];

				return inventory.preorderable && inventory.ats !== 0 && inventory.stockLevel === 0;
			}
		}

		const variants = this.getVariants();

		// False if the product doesn't have variants
		if (!variants?.length) {
			return false;
		}

		if (allVariants) {
			return variants?.every((variant) => variant.isPreOrderShown());
		} else {
			return variants?.some((variant) => variant.isPreOrderShown());
		}
	}

	/**
	 * Returns the out of stock status for the product.
	 * @returns {Boolean} True if the product is out of stock
	 */
	isOutOfStock(): boolean {
		const variants = this.getVariants();

		if (!variants?.length) {
			return false;
		}

		return variants.every((variant) => variant?.isOutOfStock());
	}

	/**
	 * Returns true if the product has at least one variant with ats > 0
	 * @returns {Boolean}
	 */
	hasAvailableVariant(): boolean {
		return this.product?.c_hasAvailableVariant ?? this.product?.inventory?.ats > 0;
	}

	/**
	 * Returns out of stock level (from inventory, NOT ats) for the current variant.
	 * @returns {Boolean} True if the current variant is out of stock level (from inventory, NOT ats)
	 */
	isOutOfStockLevel(): boolean {
		const variants = this.getVariants();

		if (!variants?.length) {
			return false;
		}

		return variants.every((variant) => variant?.isOutOfStockLevel());
	}

	/**
	 * Returns the possibility of only preorder the product.
	 * @returns {Boolean} True if all variants are combination of out of stock and preorderable
	 */
	isPreorderOnly(): boolean {
		return (
			this.hasPreorder() &&
			this.getVariants()?.every((variantModel) => variantModel?.isOutOfStock() || variantModel.isPreorder())
		);
	}

	/**
	 * Returns the food type status for the product.
	 * @returns {Boolean} True if the product has food type
	 */
	isFoodType(): boolean {
		return this.product?.c_isFood;
	}

	/**
	 * Returns the alcohol type status for the product.
	 * @returns {Boolean} True if the product has alcohol type
	 */
	isAlcoholType(): boolean {
		return this.product?.c_isAlcohol;
	}

	/**
	 * Returns product recommendations.
	 * @param {RecommendationTypes} type Selected recommendation type
	 * @returns {Array<ProductRecommendation>} Array of product recommendations
	 */
	getRecommendations(type: RecommendationTypes): Array<ProductRecommendation> {
		const recommendedProducts =
			this.product?.recommendations?.filter(
				(productRecommendation) => productRecommendation.recommendationType.value === type,
			) || [];

		return recommendedProducts;
	}

	/**
	 * Returns a string with the variation attribute value
	 * @param {String} variationAttributeName the name of the variation attribute
	 * @returns {String} variation attribute name
	 */
	getVariationValue(variationAttributeName: string): string {
		return this.product?.variationValues?.hasOwnProperty(variationAttributeName)
			? this.product?.variationValues[variationAttributeName]
			: '';
	}

	/**
	 * Checks if the product has width (length/sleeve) variation attribute.
	 * This is used for products like dress shirts where width represents sleeve length.
	 * @returns {boolean} True if the product has width variation attribute
	 */
	hasWidthVariation(): boolean {
		return this.product?.variationAttributes?.some((attr) => attr.id === 'width')
			|| this.product?.variants?.some((variant) => !!variant.variationValues?.width)
			|| false;
	}

	/**
	 * Returns the base variant object contained in the master.
	 * @returns {Array<Variant>} variant objet
	 */
	getVariantsObjects(): Array<Variant> {
		const searchRefinementSizeSorting = this.managerRepository
			.getConfigurationsMgr()
			.get('organizationPreferences.SearchRefinementSizeSorting');

		let variants = this?.product?.variants || [];

		if (searchRefinementSizeSorting && variants) {
			const sortingPattern = searchRefinementSizeSorting.split(';');

			variants = [...variants];

			variants.sort((obj1, obj2) => {
				const size1 = obj1.variationValues.size;
				const size2 = obj2.variationValues.size;
				let toCompare1 = sortingPattern.indexOf(size1);
				let toCompare2 = sortingPattern.indexOf(size2);

				if (toCompare1 === -1 || toCompare2 === -1) {
					// Fallback on sizes natural order
					toCompare1 = size1;
					toCompare2 = size2;
				}

				return toCompare1 > toCompare2 ? 1 : toCompare1 < toCompare2 ? -1 : 0;
			});
		}

		return variants;
	}

	/**
	 * Returns the product variants
	 * @param {boolean} distinctSizes - returns only one variation per variation value size
	 * @param {string | null} length - length of the product
	 * @returns {VariantModel[]} product variants
	 */
	getVariants(distinctSizes: boolean = PRODUCT_DISTINCT_SIZES, length: string = null): VariantModel[] {
		const searchRefinementSizeSorting = this.managerRepository
			.getConfigurationsMgr()
			.get('organizationPreferences.SearchRefinementSizeSorting');
		let variantsRaw: Variant[] | ProductType[] = [];

		if (this.isMaster()) {
			variantsRaw = this.product?.variants;
		} else if (
			this.product?.fullVariants?.length > 0
			&& this.product?.variants.length ===  this.product.fullVariants?.length
		) {
			variantsRaw = this.product.fullVariants;
		} else if (this.product?.variants?.length > 0) {

			variantsRaw = this.product?.variants.map((variant) => {
				return {
					...variant,
					id: variant.productId,
					currency: this.product.currency,
					variationGroups: this.product.variationGroups,
					c_variationGroupsColorGroup: this.product.c_variationGroupsColorGroup,
					c_productType: this.product.c_productType,
					c_variationGroupsHexColor: this.product.c_variationGroupsHexColor,
					c_showStockThreshold: this.product.c_showStockThreshold,
					c_hasAvailableVariant: this.product.c_hasAvailableVariant,
					c_isAlcohol: this.product.c_isAlcohol,
					c_isFood: this.product.c_isFood,
					c_size: variant?.variationValues?.size,
					c_color: variant?.variationValues?.color,
				};
			});
		}

		let variants =
			variantsRaw?.map((variant) => new VariantModel(this.managerRepository, variant)) || [];

		if (searchRefinementSizeSorting && variants) {
			const sortingPattern = searchRefinementSizeSorting.split(';');

			variants.sort((obj1, obj2) => {
				const size1 = obj1.getSize();
				const size2 = obj2.getSize();
				let toCompare1 = sortingPattern.indexOf(size1);
				let toCompare2 = sortingPattern.indexOf(size2);

				if (toCompare1 === -1 || toCompare2 === -1) {
					// Fallback on sizes natural order
					toCompare1 = size1;
					toCompare2 = size2;
				}

				return toCompare1 > toCompare2 ? 1 : toCompare1 < toCompare2 ? -1 : 0;
			});
		}

		if (distinctSizes) {
			variants = variants.reduce((newArray, variant, index) => {
				if (length && variant.getWidth() !== length) {
					return newArray;
				}

				const variationValue = variant.getVariationValue('size');
				const alreadyAdded = newArray.find((v) => v.getVariationValue('size') === variationValue);

				if (alreadyAdded) {
					return newArray;
				} else if (variant.getAtsStockLevel() > 0) {
					return [...newArray, variant];
				} else {
					const inStockAlternative = variants.find(
						(v, i) =>
							i !== index && v.getVariationValue('size') === variationValue && v.getAtsStockLevel() > 0,
					);

					return [...newArray, inStockAlternative ? inStockAlternative : variant];
				}
			}, []);
		}

		return variants;
	}

	/**
	 * Returns full variants
	 * @returns {ProductType[]}
	 */
	getFullVariants(): ProductType[] {
		return this.product?.fullVariants || [];
	}

	/**
	 * Returns the first orderable duplicate variant by sold order.
	 * @returns {VariantModel} - variant
	 */
	getOrderableVariantBySoldOrder(): VariantModel {
		const variants =
			this.product?.fullVariants?.map((variant) => new VariantModel(this.managerRepository, variant));

		if (variants && variants.length) {
			const sizes = variants.reduce((acc, v) => {
				acc[v.getVariationValue('size')] = true;

				return acc;
			}, {});

			if (Object.keys(sizes).length === 1) {
				// ascending sort with 'falsy' values coming last
				variants.sort(function (product1, product2) {
					if (!product1.getSoldOrder()) {
						return 1;
					}

					if (!product2.getSoldOrder()) {
						return -1;
					}

					return product1.getSoldOrder() - product2.getSoldOrder();
				});

				return variants.find((v) => v.isOrderable());
			}
		}

		return;
	}

	/**
	 * Returns the price model
	 */
	getPriceModel() {
		if (!this.isMaster()) {
			return this.priceModel;
		}

		const singleProductId = this.getSingleVariant().productId;

		return singleProductId
			&& (this.managerRepository.getProductsMgr().getProduct(singleProductId) as ProductModel)?.priceModel;
	}

	/**
	 * Returns the promotions of the product
	 * @returns {ProductPromotionModel[]} Product Promotion
	 */
	getProductPromotions(): ProductPromotionModel[] {
		const variants = this.getVariants();
		const variantProductPromotions = [];

		variants &&
			variants.forEach((variant) => {
				const promotions = variant.getProductPromotions();
				promotions.forEach((promotion) => {
					if (!variantProductPromotions.find(({promotionId}) => promotionId === promotion.promotionId)) {
						variantProductPromotions.push(new ProductPromotionModel(promotion));
					}
				});
			});

		if (variantProductPromotions?.length > 0) {
			return variantProductPromotions;
		}

		return this.product?.productPromotions?.map((productPromotion) => new ProductPromotionModel(productPromotion));
	}

	/**
	 * Returns price model for the product.
	 */
	get priceModel() {
		return new ProductPriceModel(this);
	}

	/**
	 * Returns the product variant by size
	 * @param {String} size - size
	 * @returns {VariantModel} product variant
	 */
	getVariantBySize(size: string): VariantModel {
		return this.getVariants().find((variant) => variant.getSize() === size);
	}

	/**
	 * Returns first variant.
	 * @returns {VariantModel} first variant
	 */
	getSingleVariant(): VariantModel {
		const variants = this.getVariants();

		return variants.length > 0 ? variants[0] : new VariantModel(this.managerRepository, {} as ProductType);
	}

	/**
	 * Returns the 3D Model Config
	 * @type {Object}
	 * @returns {Object} 3D Model Config
	 */
	get3DModelConfig(): ThreeDimensionalModelConfig {
		try {
			return JSON.parse(this.product?.c_3dModelConfig);
		} catch (e) {
			return {pois: []};
		}
	}

	/**
	 * Returns the model custom attribute of the product.
	 * @returns {String} model value
	 */
	getModelValue(): string {
		return this.product?.c_model;
	}

	/**
	 * Returns the availableForCustomerGroupA custom attribute of the product.
	 * @returns {String} availableForCustomerGroupA value
	 */
	getAvailableForCustomerGroupA(): string {
		return this.product?.c_availableForCustomerGroupA;
	}

	/**
	 * Checks if the product is ready to be displayed.
	 * @returns {Boolean} - true if the product is ready to be displayed
	 */
	isReady(): boolean {
		return super.isReady() && this.product?.ready && this.getVariants().every((v) => v.isReady());
	}
}

/**
 * Guard for ProductModel
 *
 * @param product any
 * @returns product is ProductModel
 */
export function isProductModel(product: any): product is ProductModel {
	return product instanceof ProductModel;
}

export default ProductModel;
